import { Dir } from '@angular/cdk/bidi';
import type { ConnectedPosition } from '@angular/cdk/overlay';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import {
  ChangeDetectorRef,
  ComponentRef,
  effect,
  EffectRef,
  ElementRef,
  Injectable,
  Injector,
  isSignal,
  NgZone,
  OnDestroy,
  Optional,
  runInInjectionContext,
  Signal,
  SkipSelf,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import type { Observable } from 'rxjs';
import { BehaviorSubject, EMPTY, merge, Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';

import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  WindowRef,
} from '@core/user-interface-legacy';
import type {
  ArrowPosition,
  ComponentPositionPair,
  ComponentTooltipServiceConfig,
} from '@core/user-interface-types';
import { ComponentTooltipPosition, ObserveOnElement } from '@core/user-interface-types';

import { ComponentTooltipResizeConsumer } from './resize-providers';

@Injectable()
export class ComponentTooltipService<
  C extends Type<{
    ngOnDestroy$: Observable<void>;
    updateArrow: (arrowPosition: ArrowPosition) => void;
  }>,
  T,
> implements OnDestroy
{
  public connectedOverlayDirective: ConnectedOverlayDirective;
  public data$: BehaviorSubject<T | null> = new BehaviorSubject(null);

  private component: C;
  private componentData: T;
  private componentRef: ComponentRef<{
    ngOnDestroy$: Observable<void>;
    updateArrow: (arrowPosition: ArrowPosition) => void;
  }> | null = null;
  private config: ComponentTooltipServiceConfig;
  private destroyTooltip$ = new Subject<void>();
  private mutationObserver = new MutationObserver(() => this.updateTooltipPosition());
  private observeEffect: EffectRef | null = null;

  constructor(
    private elementRef: ElementRef,
    private overlay: Overlay,
    private vcRef: ViewContainerRef,
    private _cdRef: ChangeDetectorRef,
    private injector: Injector,
    private _ngZone: NgZone,
    private router: Router,
    private windowRef: WindowRef,
    @SkipSelf() private parentCdRef: ChangeDetectorRef,
    @Optional() private _scrollDispatcher: ScrollDispatcher,
    @Optional() private _dir: Dir,
    @Optional() private componentTooltipResize?: ComponentTooltipResizeConsumer,
  ) {}

  public ngOnDestroy(): void {
    this.componentRef = null;
    this.closeTooltip();
    this.connectedOverlayDirective?.ngOnDestroy();
  }

  public hasConfig(): boolean {
    return !!this.config;
  }

  public setConfig(config: ComponentTooltipServiceConfig): void {
    this.config = config;
  }

  public setComponent(component: C): void {
    this.component = component;
  }

  public setComponentData(data: T): void {
    this.componentData = data;
    this.data$.next(data);
  }

  public openTooltip(originRef?: ElementRef): ComponentRef<InstanceType<C>> {
    if (!this.connectedOverlayDirective || originRef) {
      this.connectedOverlayDirective = this.setupConnectedOverlayDirective();
      this.watchPositionUpdateEvents();
    }

    const portal = new ComponentPortal(this.component, null, this.injector);
    this.connectedOverlayDirective.positions = this.getPositions();
    this.connectedOverlayDirective.origin = new OverlayOriginDirective(
      originRef || this.elementRef,
    );
    this.connectedOverlayDirective.portal = portal;
    this.componentRef = <ComponentRef<InstanceType<C>>>(
      this.connectedOverlayDirective.toggleOverlay(true)
    );
    this.connectedOverlayDirective.positionStrategy.withPush(this.config.withPush ?? true);
    this.connectedOverlayDirective.positionChange
      .pipe(take(1))
      .subscribe(() => this.updateTooltipPosition());
    this.startObserveOnElement();
    this.updateTooltipPosition();
    return <ComponentRef<InstanceType<C>>>this.componentRef;
  }

  public closeTooltip(): void {
    this.destroyTooltip$.next();

    if (this.connectedOverlayDirective) {
      this.connectedOverlayDirective.open = false;
    }
    this.mutationObserver.disconnect();
    this.destroyObserveEffect();
    this.componentRef = null;
  }

  public isOpen() {
    return !!this.connectedOverlayDirective?.open;
  }

  public updateTooltipPosition() {
    const availablePositions = this.getPositions();
    const { positionStrategy } = this.connectedOverlayDirective;
    if (!positionStrategy) return;

    this.connectedOverlayDirective.overlayRef.updatePosition();
    const position = positionStrategy['_lastPosition'];
    if (!position) return;

    const compareKeys = <const>['originX', 'originY', 'overlayX', 'overlayY'];
    const positionWithArrow = availablePositions.find(pos =>
      compareKeys.every(key => pos[key] === position[key]),
    );
    if (positionWithArrow) {
      this.lockTooltipPosition(positionWithArrow);
      if (positionWithArrow.offsetX) {
        this.connectedOverlayDirective.offsetX = positionWithArrow.offsetX;
      }
      if (positionWithArrow.offsetY) {
        this.connectedOverlayDirective.offsetY = positionWithArrow.offsetY;
      }
      this.connectedOverlayDirective.overlayRef.updatePosition();

      if (this.componentRef) {
        const { instance, changeDetectorRef } = this.componentRef;
        instance.updateArrow(positionWithArrow.arrowPosition);
        changeDetectorRef.detectChanges();
      }
    }
  }

  private setupConnectedOverlayDirective() {
    const connectedOverlayDirective = runInInjectionContext(
      this.injector,
      () =>
        new ConnectedOverlayDirective(
          null,
          this.vcRef,
          this.overlay,
          this._cdRef,
          this._ngZone,
          this.parentCdRef,
          this._scrollDispatcher,
          this._dir,
        ),
    );
    connectedOverlayDirective.hasBackdrop = this.config.hasBackdrop;
    connectedOverlayDirective.backdropClass = this.config.backdropClass || '';
    connectedOverlayDirective.autoCloseOnUnpreventedClick = false;
    connectedOverlayDirective.position = Object.values(ComponentTooltipPosition);
    connectedOverlayDirective.overlayClipArea = this.config.overlayClipArea;

    return connectedOverlayDirective;
  }

  private getPositions() {
    return <ComponentPositionPair[]>Object.values(this.config.positions);
  }

  private watchPositionUpdateEvents(): void {
    merge<unknown[]>(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
      this.windowRef.resize$,
      this.componentTooltipResize ?? EMPTY,
    )
      .pipe(debounceTime(5), takeUntil(this.destroyTooltip$))
      .subscribe(() => this.updateTooltipPosition());
  }

  private getOverlayElement(): HTMLElement {
    return this.connectedOverlayDirective.overlayRef.overlayElement;
  }

  private lockTooltipPosition(position: ConnectedPosition) {
    const { positionStrategy } = this.connectedOverlayDirective;
    if (positionStrategy) {
      this.connectedOverlayDirective.positions = [position];
      positionStrategy._preferredPositions = [position];
    }
  }

  private startObserveOnElement(): void {
    const { observeOnElement, observeOnCustomElement } = this.config;
    const isCustomInvalid = ObserveOnElement.Custom === observeOnElement && !observeOnCustomElement;
    this.destroyObserveEffect();
    if (!observeOnElement || isCustomInvalid) return;

    // eslint-disable-next-line default-case
    switch (observeOnElement) {
      case ObserveOnElement.Self:
        return this.observe(this.elementRef.nativeElement);
      case ObserveOnElement.Parent:
        return this.observe(this.elementRef.nativeElement.parentElement);
      case ObserveOnElement.Custom: {
        if (!isSignal(observeOnCustomElement)) return this.observe(observeOnCustomElement!);

        this.observeEffect = this.createObserveEffect(observeOnCustomElement);
      }
    }
  }

  private createObserveEffect(elementRef: Signal<ElementRef<Node>>) {
    return effect(
      onCleanup => {
        this.observe(elementRef().nativeElement);

        onCleanup(() => this.mutationObserver.disconnect());
      },
      { injector: this.injector },
    );
  }

  private destroyObserveEffect() {
    this.observeEffect?.destroy();
    this.observeEffect = null;
  }

  private observe(node: Node) {
    this.mutationObserver.disconnect();
    this.mutationObserver.observe(node, { attributes: true, subtree: true });
  }
}
